import { useToast } from '@airship-platform/airship-patterns';
import { Product, useManageProducts } from '@airship-platform/airship-proposal';
import { LoadingButton } from '@airship-platform/jam-web/components';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../../data-access/appointmentProposalOptions';
import ROUTE_PATHS from '../../../routes/paths';

interface ReplaceItemButtonProps {
  product: Product;
  replaceProductId: string;
}

const ReplaceItemButton: React.FC<ReplaceItemButtonProps> = ({
  product,
  replaceProductId,
}) => {
  const {
    proposalProducts,
    createAppointmentProposalOptionProduct,
    deleteAppointmentProposalOptionProduct,
    getNextSortOrder,
    error: productsError,
  } = useAppointmentProposalOptionProducts();
  const { currentOption, error: optionsError } =
    useAppointmentProposalOptions();
  const { productDefaults } = useManageProducts();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [isReplacing, setIsReplacing] = useState(false);

  const oldProduct = useMemo(
    () =>
      proposalProducts.find(
        (p) => p.appointmentProposalOptionProductId === replaceProductId
      ),
    [replaceProductId, proposalProducts]
  );

  if (!currentOption || !oldProduct) {
    return null;
  }

  const onClick = async () => {
    setIsReplacing(true);
    const oldPriceType = oldProduct.defaultPriceType;
    const newHasOldPriceType = product.prices.some(
      (p) => p.priceType === oldPriceType
    );
    const defaultPriceType = newHasOldPriceType
      ? oldPriceType
      : product.defaultPriceType;

    const defaultPrice = product.prices.find(
      (p) => p.priceType === defaultPriceType
    );

    await createAppointmentProposalOptionProduct(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      currentOption.appointmentProposalOptionId,
      {
        ...product,
        ...productDefaults,
        pricebookPrices: product.prices,
        jobId: currentOption.jobId,
        currency: 'USD',
        quantity: oldProduct.quantity || 1,
        price: defaultPrice?.value || 0,
        defaultPriceType,
        taxable: defaultPrice?.taxable || false,
        linkedItems: product.linkedItems.filter(
          (item) =>
            item.priceType ===
            (defaultPriceType === 'DYNAMIC' ? 'STANDARD' : defaultPriceType)
        ),
        sortOrder: oldProduct.sortOrder ?? getNextSortOrder(),
      }
    );

    await deleteAppointmentProposalOptionProduct(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      currentOption.appointmentProposalOptionId,
      oldProduct.appointmentProposalOptionProductId
    );

    showToast(`Replaced with ${product.name}`, 'success');
    navigate(ROUTE_PATHS.PROPOSAL_OPTIONS_PATH(currentOption));
  };

  return (
    <LoadingButton
      color="softGold"
      loading={!productsError && !optionsError && isReplacing}
      disabled={currentOption.isReadOnly}
      onClick={onClick}
    >
      Replace
    </LoadingButton>
  );
};

export default ReplaceItemButton;
